<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="$t('create')"
        :create-mode-text="$t('product.info.create_flight_number')"
        :edit-mode-text="$t('product.info.edit_flight_number')"
        @reset="editFlightNumberId = null"
    >
        <template v-slot:btn-block>
            <div class="full-width d-flex align-center">
                <v-spacer />
                <FilterStatusToggle v-model="activeFilter" />
            </div>
        </template>
        <template v-slot:main-mode>
            <v-data-table
                :headers="useHeaders"
                :items="useFilteredFlightNumbers"
                :items-per-page="-1"
                :items-per-page-options="[-1]"
                :loading="isLoading"
                :items-length="useFilteredFlightNumbers.length"
                disable-sort
                :sort-by="[{key: 'name', order: 'asc'}]"
                must-sort
                density="compact"
                color="primary"
                class="elevation-1 table-server"
                data-cy="flight-number-table"
            >
                <template v-slot:item.name="{item}">
                    <div class="text-link" @click="switchToEditMode(item.id)">
                        {{ item.name }}
                    </div>
                </template>
            </v-data-table>
        </template>
        <template v-slot:additional-mode>
            <InfoFlightNumberForm
                :edit-flight-number-id="editFlightNumberId"
                :contract-id="contractId"
                @save="save()"
                @cancel="modeSwitcher.goToTable()"
            />
        </template>
    </ModeSwitcher>
</template>

<script setup lang="ts">
    import InfoFlightNumberForm from '~/components/products/information/InfoFlightNumberForm.vue'

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        contractId: number
    }>()

    const isLoading = ref<boolean>(false)
    const flightNumbers = ref<any[]>([])
    const modeSwitcher = ref(null)
    const editFlightNumberId = ref<number>(null)
    const activeFilter = ref<boolean>(true)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {contractId: props.contractId}
            flightNumbers.value = await app.$api.ownCharterServiceCategories.get(rq)
        } catch (e) {
            flightNumbers.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('flight_number'), value: 'name', minWidth: '120px', sortable: true},
            {title: t('flights.service_classes'), value: 'serviceClasses', minWidth: '120px'},
            {title: t('flights.direction'), value: 'direction', minWidth: '120px'},
        ]
        return headers
    })

    const useItems = computed(() => {
        return flightNumbers.value.map(({name, direction, id, apiOwnCharterServiceCategories, active}) => ({
            active,
            id,
            name,
            serviceClasses: apiOwnCharterServiceCategories
                .filter(({active}) => active)
                .map(({serviceCategoryClass}) => t(`flights.serviceClass.${serviceCategoryClass}`))
                .join(', '),
            direction: t(`flights.flightDirection.${direction}`),
        }))
    })

    const switchToEditMode = (id: number): void => {
        modeSwitcher.value.edit()
        editFlightNumberId.value = id
    }

    const useFilteredFlightNumbers = computed(() => {
        return useItems.value.filter(({active}) => activeFilter.value === active)
    })
</script>
